import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Select, Spin, message } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useQueryClient } from 'react-query';
import { useGroupGiftOrderDetail, useLogistics, useUpdateGroupGiftOrder } from 'hooks';
import { numberWithCommas } from 'utils';
import { ORDER_STATUS } from 'interfaces/bizGift';
import { DeleteButton, DetailRow, DetailTableWrapper, ShippingEditArea, ShippingEditNotice, StatusText } from './styles';
import { addPhoneHyphen, formatDate } from './helpers';

interface OrderDetailModalProps {
  groupGiftOrderIdx: number | null;
  onClose: () => void;
}

const OrderDetailModal = ({ groupGiftOrderIdx, onClose }: OrderDetailModalProps) => {
  const [logisticsCode, setLogisticsCode] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');

  const queryClient = useQueryClient();
  const { data: logistics = [] } = useLogistics();
  const { data: detail, isFetching } = useGroupGiftOrderDetail(groupGiftOrderIdx ?? undefined);
  const { mutate: updateOrder, isLoading: isSaving } = useUpdateGroupGiftOrder();

  useEffect(() => {
    setLogisticsCode(detail?.logisticsCode || '');
    setTrackingNumber(detail?.trackingNumber || '');
  }, [detail]);

  const submit = (saveData: { logisticsCode: string | null; trackingNumber: string | null }, successMessage: string) => {
    if (groupGiftOrderIdx === null) return;
    updateOrder(
      { groupGiftOrderIdx, saveData },
      {
        onSuccess: () => {
          message.success(successMessage);
          // 목록 갱신 + 상세 재조회(모달은 열린 채 최신 정보로 갱신)
          queryClient.invalidateQueries(['getGroupGiftOrders']);
          queryClient.invalidateQueries(['getGroupGiftOrderDetail', groupGiftOrderIdx]);
        }
      }
    );
  };

  const handleDelete = () => submit({ logisticsCode: null, trackingNumber: null }, '배송정보가 삭제되었습니다.');

  const handleSave = () => submit({ logisticsCode, trackingNumber }, '배송정보가 저장되었습니다.');

  const rows = detail
    ? [
        { label: '주문번호', value: detail.groupGiftOrderIdx },
        { label: '회사명', value: detail.companyName },
        { label: '상품코드', value: detail.productCode },
        { label: '상품명', value: detail.productName },
        { label: '상품옵션', value: detail.productOption || '-' },
        { label: '공급금액', value: numberWithCommas(detail.supplyPrice) },
        {
          label: '상태',
          value: <StatusText $color={ORDER_STATUS[detail.status].color}>{ORDER_STATUS[detail.status].label}</StatusText>
        },
        { label: '보내는사람', value: detail.senderName },
        { label: '보내는사람 도로명주소', value: detail.senderRoadAddress || '-' },
        { label: '보내는사람 지번주소', value: detail.senderJibunAddress || '-' },
        { label: '보내는사람 상세주소', value: detail.senderAddressDetail || '-' },
        { label: '보내는사람 우편번호', value: detail.senderZipCode || '-' },
        { label: '고객사 요청사항', value: detail.companyRequirement || '-' },
        { label: '받는사람', value: detail.receiverName },
        { label: '받는사람 전화번호', value: addPhoneHyphen(detail.receiverPhone) },
        { label: '받는사람 도로명주소', value: detail.receiverRoadAddress || '-' },
        { label: '받는사람 지번주소', value: detail.receiverJibunAddress || '-' },
        { label: '받는사람 상세주소', value: detail.receiverAddressDetail || '-' },
        { label: '받는사람 우편번호', value: detail.receiverZipCode || '-' },
        { label: '배송 요청사항', value: detail.deliveryRequirement || '-' },
        { label: '주문 확정일', value: formatDate(detail.confirmDate) },
        { label: '출고 희망일', value: formatDate(detail.requestShipmentDate) },
        { label: '택배사', value: detail.logisticsName || '-' },
        { label: '송장번호', value: detail.trackingNumber || '-' }
      ]
    : [];

  return (
    <Modal
      title="배송정보 수정"
      open={groupGiftOrderIdx !== null}
      width={640}
      onCancel={onClose}
      footer={
        <>
          <Button onClick={onClose}>닫기</Button>
          <Button type="primary" onClick={handleSave} loading={isSaving}>
            저장
          </Button>
        </>
      }
    >
      <Spin spinning={isFetching}>
        <DetailTableWrapper>
          {rows.map(({ label, value }) => (
            <DetailRow key={label}>
              <div className="detail-label">{label}</div>
              <div className="detail-value">{value}</div>
            </DetailRow>
          ))}
        </DetailTableWrapper>
        <ShippingEditArea>
          <div className="field">
            <span className="field-label">택배사</span>
            <Select
              style={{ width: 200 }}
              placeholder="선택"
              value={logisticsCode || undefined}
              onChange={(value) => setLogisticsCode(value)}
            >
              {logistics.map((item) => (
                <Select.Option key={item.code} value={item.code}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="field">
            <span className="field-label">송장번호</span>
            <Input
              style={{ width: 310, height: 40 }}
              placeholder="송장번호 입력"
              type="number"
              maxLength={50}
              value={trackingNumber}
              onChange={({ target }) => setTrackingNumber(target.value)}
            />
          </div>
          <DeleteButton onClick={handleDelete} loading={isSaving}>
            삭제
          </DeleteButton>
        </ShippingEditArea>
        <ShippingEditNotice>
          <InfoCircleOutlined />
          송장번호와 택배사를 저장하면 배송중으로 자동 변경됩니다
        </ShippingEditNotice>
      </Spin>
    </Modal>
  );
};

export default OrderDetailModal;
